import { useState, FormEvent, useRef, useCallback } from 'react';
import emailjs from '@emailjs/browser';
import { supabase } from '@/lib/supabase';

const EMAILJS_SERVICE_ID = 'service_ysy965c';
const EMAILJS_TEMPLATE_ID = 'template_u8mbifq';
const EMAILJS_PUBLIC_KEY = 'vODGfI5-jZ-SaoIxd';

const MAX_PHOTOS = 10;
const MAX_FILE_SIZE_MB = 10;

interface PhotoFile {
  file: File;
  preview: string;
}

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addPhotos = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((f) => {
      if (!f.type.startsWith('image/')) return false;
      if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) return false;
      return true;
    });

    const newPhotos: PhotoFile[] = validFiles.slice(0, MAX_PHOTOS - photos.length).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setPhotos((prev) => [...prev, ...newPhotos].slice(0, MAX_PHOTOS));
  }, [photos.length]);

  const removePhoto = (index: number) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addPhotos(e.dataTransfer.files);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      const name = (form.elements.namedItem('from_name') as HTMLInputElement)?.value || '';
      const phone = (form.elements.namedItem('phone') as HTMLInputElement)?.value || '';
      const email = (form.elements.namedItem('from_email') as HTMLInputElement)?.value || '';
      const vehicle = (form.elements.namedItem('vehicle') as HTMLInputElement)?.value || '';
      const insurance = (form.elements.namedItem('insurance') as HTMLInputElement)?.value || '';

      // Upload photos to Supabase Storage
      const photoUrls: string[] = [];
      if (photos.length > 0) {
        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];
          const ext = photo.file.name.split('.').pop() || 'jpg';
          const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
          const filePath = `${Date.now()}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('estimate-photos')
            .upload(filePath, photo.file, { contentType: photo.file.type });

          if (!uploadError) {
            photoUrls.push(filePath);
          }
          setUploadProgress(Math.round(((i + 1) / photos.length) * 60));
        }
      }

      setUploadProgress(70);

      // Save to Supabase DB
      const { error: dbError } = await supabase.from('estimates').insert({
        full_name: name,
        phone,
        email,
        vehicle,
        insurance,
        message,
        photo_urls: photoUrls,
        status: 'pending',
      });

      if (dbError) throw new Error(dbError.message);

      setUploadProgress(85);

      // Send email with photo links via Edge Function
      await fetch('https://sksgkyjrthinowaagdkq.supabase.co/functions/v1/send-estimate-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, email, vehicle, insurance, message, photoUrls }),
      });

      // Also send via EmailJS as backup notification
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: name,
          from_email: email,
          phone,
          vehicle,
          insurance,
          message,
          photos_info: photoUrls.length > 0
            ? `${photoUrls.length} foto(s) subida(s) — ver links en el email principal`
            : 'No se subieron fotos',
          reply_to: email,
        },
        EMAILJS_PUBLIC_KEY,
      );

      setUploadProgress(100);
      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      setError('There was an error sending your message. Please call us directly at (760) 666-4151.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0c0c0c] py-28 md:py-36 w-full">
      <div className="max-w-[1600px] mx-auto px-6 md:px-16">
        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-yellow-600 text-xs tracking-[0.4em] uppercase font-light">Reach Out</span>
          <h2 className="font-serif text-4xl md:text-5xl text-white mt-4 mb-5">
            Get In <span className="text-yellow-400 italic">Touch</span>
          </h2>
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-yellow-600"></div>
            <div className="w-2 h-2 rounded-full bg-yellow-600"></div>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-yellow-600"></div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: Info */}
          <div className="flex flex-col gap-10">
            {[
              {
                icon: 'ri-phone-line',
                title: '(760) 666-4151',
                sub: 'Call us during business hours',
                href: 'tel:7606664151',
              },
              {
                icon: 'ri-mail-line',
                title: 'estimates@theroyalcollisioncentre.com',
                sub: 'We respond within 24 hours',
                href: 'mailto:estimates@theroyalcollisioncentre.com',
              },
              {
                icon: 'ri-map-pin-line',
                title: '2127 E Valley Pkwy, Escondido, CA 92027',
                sub: 'Visit our facility',
                href: 'https://www.google.com/maps/place/The+Royal+Collision+Centre/@33.1386319,-117.0509717,17z',
              },
              {
                icon: 'ri-time-line',
                title: 'Monday \u2013 Friday: 7:00 AM \u2013 5:00 PM',
                sub: 'Saturday & Sunday: Closed',
                href: null,
              },
              {
                icon: 'ri-instagram-line',
                title: '@theroyalcollisioncentre',
                sub: 'Follow us on Instagram',
                href: 'https://www.instagram.com/theroyalcollisioncentre/',
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-full border border-yellow-700/40 bg-yellow-900/10 flex-shrink-0">
                  <i className={`${item.icon} text-xl text-yellow-500`}></i>
                </div>
                <div>
                  {item.href ? (
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer nofollow' : undefined}
                      className="text-white text-base hover:text-yellow-400 transition-colors duration-300 cursor-pointer break-all"
                    >
                      {item.title}
                    </a>
                  ) : (
                    <div className="text-white text-base">{item.title}</div>
                  )}
                  <div className="text-white/40 text-xs tracking-wide mt-1">{item.sub}</div>
                </div>
              </div>
            ))}

            {/* Map */}
            <div className="rounded-xl overflow-hidden border border-yellow-700/20 h-64 mt-2">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3318.4!2d-117.0509717!3d33.1386319!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dbf589bc84e7d9%3A0xf98910bc12ca38f3!2sThe%20Royal%20Collision%20Centre!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="The Royal Collision Centre location map"
              ></iframe>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-[#111111] rounded-2xl border border-yellow-700/20 p-8 md:p-12">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-yellow-500 mb-6">
                  <i className="ri-check-line text-3xl text-yellow-500"></i>
                </div>
                <h3 className="font-serif text-2xl text-white mb-3">Message Received</h3>
                <p className="text-white/50 text-sm leading-relaxed max-w-xs">
                  Thank you for reaching out. Our team will contact you within 24 hours to discuss your vehicle&apos;s needs.
                </p>
              </div>
            ) : (
              <form
                ref={formRef}
                id="contact-estimate-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <h3 className="font-serif text-2xl text-white mb-2">Request an Estimate</h3>
                <p className="text-white/40 text-xs tracking-wide mb-4">All fields help us serve you better.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-yellow-600/70 text-xs tracking-widest uppercase mb-2 block">Full Name</label>
                    <input
                      type="text"
                      name="from_name"
                      required
                      placeholder="Your name"
                      className="w-full bg-[#1a1a1a] border border-yellow-700/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-yellow-600/60 transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="text-yellow-600/70 text-xs tracking-widest uppercase mb-2 block">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(xxx) xxx-xxxx"
                      className="w-full bg-[#1a1a1a] border border-yellow-700/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-yellow-600/60 transition-colors duration-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-yellow-600/70 text-xs tracking-widest uppercase mb-2 block">Email</label>
                    <input
                      type="email"
                      name="from_email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-[#1a1a1a] border border-yellow-700/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-yellow-600/60 transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="text-yellow-600/70 text-xs tracking-widest uppercase mb-2 block">Vehicle</label>
                    <input
                      type="text"
                      name="vehicle"
                      placeholder="Year, Make, Model"
                      className="w-full bg-[#1a1a1a] border border-yellow-700/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-yellow-600/60 transition-colors duration-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-yellow-600/70 text-xs tracking-widest uppercase mb-2 block">Insurance Provider</label>
                  <input
                    type="text"
                    name="insurance"
                    placeholder="Insurance company name (if applicable)"
                    className="w-full bg-[#1a1a1a] border border-yellow-700/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-yellow-600/60 transition-colors duration-300"
                  />
                </div>

                <div>
                  <label className="text-yellow-600/70 text-xs tracking-widest uppercase mb-2 block">
                    Message
                    <span className={`ml-2 normal-case ${charCount > 450 ? 'text-red-400' : 'text-white/30'}`}>
                      {charCount}/500
                    </span>
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="Describe the damage or service needed..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-[#1a1a1a] border border-yellow-700/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-yellow-600/60 transition-colors duration-300 resize-none"
                  ></textarea>
                </div>

                {/* Photo Upload */}
                <div>
                  <label className="text-yellow-600/70 text-xs tracking-widest uppercase mb-2 block">
                    Damage Photos
                    <span className="ml-2 normal-case text-white/30">{photos.length}/{MAX_PHOTOS}</span>
                  </label>

                  {/* Drop Zone */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-300 ${
                      isDragging
                        ? 'border-yellow-500 bg-yellow-900/20'
                        : 'border-yellow-700/30 bg-[#1a1a1a] hover:border-yellow-600/50 hover:bg-yellow-900/10'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => e.target.files && addPhotos(e.target.files)}
                    />
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-camera-line text-2xl text-yellow-600/60"></i>
                    </div>
                    <p className="text-white/50 text-xs">
                      <span className="text-yellow-500 font-medium">Click to upload</span> or drag &amp; drop
                    </p>
                    <p className="text-white/30 text-xs mt-1">JPG, PNG, WEBP up to {MAX_FILE_SIZE_MB}MB each &bull; Max {MAX_PHOTOS} photos</p>
                  </div>

                  {/* Photo Previews */}
                  {photos.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-yellow-700/20">
                          <img
                            src={photo.preview}
                            alt={`Damage photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="whitespace-nowrap absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-red-400 text-lg"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {error && (
                  <div className="bg-red-900/20 border border-red-700/40 rounded-lg px-4 py-3 text-red-400 text-xs">
                    {error}
                  </div>
                )}

                {loading && uploadProgress > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-white/40">
                      <span>{uploadProgress < 70 ? 'Uploading photos...' : uploadProgress < 90 ? 'Saving estimate...' : 'Sending notification...'}</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1">
                      <div
                        className="bg-yellow-500 h-1 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-3 py-4 text-sm tracking-widest uppercase text-yellow-300 bg-gradient-to-r from-yellow-900/40 via-yellow-800/20 to-black border border-yellow-600/40 rounded-lg hover:border-yellow-500 hover:from-yellow-800/50 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> Send Message</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
